import { GarnerProviderNetworksClientV1 } from '@garner-health/api-client/svc-providers/provider-networks/provider-networks-v1';
import { failure, success } from '@garner-health/lib-ts-common';

import createLogger from '~/logging';

import { baseClientOptions, chunkArray, getDefaultHeaders } from './common';

const log = createLogger(__filename);

// URL can have max length of 2048 and each id is a 36 char uuid
// https://datapro.getgarner.com/api/provider-networks is 52 characters
// the repeated query param contents `&id=` add 4 characters per ID
// 40 * (36 + 4) + 52 = 1652, so we leave some buffer
const MAX_IDS_TO_INCLUDE = 40;

const MAX_CONCURRENT_REQUESTS = 5;

export type Network = {
  id: string;
  name: string;
  isHcscNetwork: boolean;
};

export class ProviderNetworksClient {
  readonly client: GarnerProviderNetworksClientV1;

  constructor(options = baseClientOptions) {
    this.client = new GarnerProviderNetworksClientV1(options);
  }

  async getNetworks(ids: string[]): Promise<Network[]> {
    if (ids.length === 0) {
      return [];
    }

    const networkIdChunks = chunkArray(ids, MAX_IDS_TO_INCLUDE);
    const allNetworks: Network[] = [];

    // Limit how many requests we send in parallel to MAX_CONCURRENT_REQUESTS.
    for (let i = 0; i < networkIdChunks.length; i += MAX_CONCURRENT_REQUESTS) {
      const batch = networkIdChunks.slice(i, i + MAX_CONCURRENT_REQUESTS);
      const results = await Promise.allSettled(batch.map(chunk => this.fetchNetworkChunk(chunk)));

      for (let j = 0; j < results.length; j++) {
        const result = results[j];
        if (result.status === 'fulfilled') {
          allNetworks.push(...result.value);
        } else {
          log.error(
            { err: result.reason, strNetworkIdChunk: batch[j] },
            'Failed to fetch network chunk - skipping chunk',
          );
        }
      }
    }

    return allNetworks;
  }

  private async fetchNetworkChunk(networkIds: string[]): Promise<Network[]> {
    try {
      const result = await this.client
        .get('/provider-networks', {
          headers: await getDefaultHeaders(),
          query: { id: networkIds },
        })
        .onStatus(200, r => success(r.json.items))
        .catch(err => {
          if (err?.status === 404 || err?.response?.status === 404) {
            return failure('notFound');
          }
          throw err;
        });

      if (!result.success) {
        log.warn(
          { context: { networkIdsCount: networkIds.length } },
          'Some networks not found (404) - recursively retrying to find specific missing networks',
        );
        return this.retryWithSmallerBatches(networkIds);
      }

      return result.data;
    } catch (err) {
      log.error({ err, context: { networkIdsCount: networkIds.length } }, 'Error getting network batch');
      throw err;
    }
  }

  private async retryWithSmallerBatches(networkIds: string[]): Promise<Network[]> {
    if (networkIds.length === 1) {
      log.warn(
        { context: { networkId: networkIds[0] } },
        'Single network not found (404) - filtering out missing network',
      );
      return [];
    }

    const midpoint = Math.ceil(networkIds.length / 2);
    const firstHalf = networkIds.slice(0, midpoint);
    const secondHalf = networkIds.slice(midpoint);

    const [firstResults, secondResults] = await Promise.all([
      this.fetchNetworkChunk(firstHalf),
      this.fetchNetworkChunk(secondHalf),
    ]);

    return [...firstResults, ...secondResults];
  }
}

export const providerNetworksClient = new ProviderNetworksClient();
